import { useBrandStore } from "../store/brand";
import { useUserStore } from "../store/user";
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";

export const useBrand = () => {
  const brandStore = useBrandStore();
  const userStore = useUserStore();
  const { t } = useI18n();
  const route = useRoute();
  const isBrandReady = ref(false);

  const getStoredBrand = () => {
    if (typeof window === "undefined") {
      return undefined;
    }

    return window.localStorage?.brand;
  };

  watch(
    () => route.params?.brand || route.params?.distributor || getStoredBrand(),
    async (value) => {
      if (userStore.isAuthenticated) {
        isBrandReady.value = true;
        return;
      }

      isBrandReady.value = false;

      //Sporttravel will use a different URL structure than the other brands, so we need to check the URL instead of the route params
      const hasWindow = typeof window !== "undefined";

      if (hasWindow && window.location.href.includes("sporttravel")) {
        try {
          await brandStore.fetchBrand("SportTravel");
        } finally {
          isBrandReady.value = true;
        }
        return;
      }

      if (!value) {
        isBrandReady.value = true;
        return;
      }

      try {
        await brandStore.fetchBrand(value);
      } finally {
        isBrandReady.value = true;
      }
    },
    { immediate: true }
  );

  //Set initial color based on loaded stylesheet
  const hasDocument = typeof document !== "undefined";
  const initialPrimaryColor = hasDocument ? getComputedStyle(document.body).getPropertyValue("--color-primary") : "";
  const initialSecondaryColor = hasDocument
    ? getComputedStyle(document.body).getPropertyValue("--color-secondary")
    : "";

  //Set initial font based on loaded stylesheet
  const initialFontFamily = hasDocument ? getComputedStyle(document.body).getPropertyValue("font-family") : "";

  const primary = computed(() => {
    if (brandStore.data?.corporate_color) {
      return brandStore.data.corporate_color;
    }

    return initialPrimaryColor;
  });

  const secondary = computed(() => {
    if (brandStore.data?.narrative_color) {
      return brandStore.data.narrative_color;
    }

    return initialSecondaryColor;
  });

  const fontFamily = computed(() => {
    if (brandStore.data?.font_family) {
      return `${brandStore.data.font_family}, sans-serif`;
    }
    return initialFontFamily;
  });

  const documentTitle = computed(() => {
    const titles = [];

    if (route.meta.title) {
      titles.push(t(route.meta.title));
    }

    if (brandStore.data?.distributor_name) {
      titles.push(brandStore.data.distributor_name);
    }

    return titles.join(" | ");
  });

  watch(
    documentTitle,
    (title) => {
      if (hasDocument) {
        document.title = title;
      }
    },
    { immediate: true }
  );
  return { primary, secondary, fontFamily, isBrandReady };
};
